import { Module, Mutation, VuexModule } from "vuex-module-decorators";

import { LogService } from "@/services";
import {
  Banner,
  Briefcase,
  BriefcaseComparator,
  BriefcaseFolder,
  BriefcaseItem,
  BriefcasePayload,
  HomepageConfig,
  LookupTableItem,
  LookupTableItemKeys,
  LookupTables,
  LookupTablesKeys,
  NestedLookupTableItem,
  NestedLookupTableItemKeys,
  NestedLookupTables,
  NestedLookupTablesKeys,
  NoteworthyGroup,
  ProfileView,
  Search,
  SearchItem,
  SearchOrder,
  SearchQuery,
  SearchQueryUpdatePayload,
  SearchResultsPagination,
  SignUpFormStatus,
  SnackbarStatus,
  SubscriptionModule,
} from "@/types";

/**
 * this.context available for normal VueX style access and operations.
 */
@Module({ namespaced: true, name: "application" })
export class ApplicationModule extends VuexModule {
  private debugModeEnabled = process.env.VUE_APP_DEBUG === "true";
  private baseUrlPath = "";
  private baseUrlOrigin = "";
  private vignetteEmailAddress = "njvignette@nationaljournal.com";
  private pageName = "";
  private loading = true;
  private navDrawerExpanded = false;
  private redirectPath = "";
  private homepageConfig: HomepageConfig = {
    isLoading: false,
    lastUpdated: null,
    featuredProfiles: [],
    featuredProfilesText: "",
    recentProfiles: [],
    noteworthyGroups: [],
    primaryBanner: { wideImg: "", smallImg: "", url: "" },
    secondaryBanner: { wideImg: "", smallImg: "", url: "" },
  };
  private search: Search = ApplicationModule._getDefaultSearch();
  private briefcase: Briefcase = ApplicationModule._getDefaultBriefcase();
  private lookupTables: LookupTables = ApplicationModule._getDefaultLookupTables();
  private nestedLookupTables: NestedLookupTables = ApplicationModule._getDefaultNestedLookupTables();
  private unlockedProfiles: number[] = [];

  private assignedUsersList: string[] = [];
  private snackbarStatus: SnackbarStatus = {
    state: false,
    message: "",
    color: "error",
    timeout: 3000,
    itemBeingMoved: null,
  };
  private signUpFormStatus: SignUpFormStatus = {
    shouldKeep: false,
    email: "",
    isDisplayForm: false,
  };

  /*
  This number is also defined on the BE within vignette/apps.py and must be updated to match this value if we want to change the limit.
  */
  private trialProfileLimit = 5;
  //***** DEFAULT OBJECTS *****//

  private static _getDefaultBriefcase(): Briefcase {
    return {
      folders: new Map<number, BriefcaseFolder>(),
      // See getBriefcaseFolders() for why we need foldersFlat variable
      foldersFlat: [],
      itemIndex: new Map<number, number[]>(),
      defaultFolderId: -1,
      currentFolderId: -1,
      currentPage: 1,
      pageSize: 12,
      isLoading: false,
      lastUpdated: null,
      itemBeingMoved: null,
    };
  }

  private static _getDefaultSearch(): Search {
    return {
      currentSearchQuery: ApplicationModule._getDefaultSearchQuery(),
      lastSearchQuery: null,
      lastSearchQueryPagination: {
        pageSize: 12,
        page: 1,
        totalPages: 1,
        totalHits: 0,
        order: SearchOrder.DEFAULT,
      },
      cachedResults: [],
      keepCachedResults: false,
      autocomplete: {
        isLoading: false,
        results: [],
      },
      isLoading: false,
    };
  }

  private static _getDefaultSearchQuery(): SearchQuery {
    return {
      // Search configuration
      page: 1,
      pageSize: 12,
      order: SearchOrder.DEFAULT,
      view: ProfileView.DEFAULT,
      hidePayWalledProfiles: undefined,

      // Local variables
      autoCompleteKeyword: "",
      advancedSearchOpen: false,

      // Vignette simple search
      keyword: "",

      // Vignette advanced search
      currentOfficeCongressionalDistrict: { id: "", name: "" },
      currentOfficeStates: [],
      politicalParty: { id: "", name: "" },
      religion: { id: "", name: "" },
      gender: { id: "", name: "" },
      ethnicity: { id: "", name: "" },
      state: { id: "", name: "" },
      country: { id: "", name: "" },
      editorialGroup: { id: "", name: "" },
      profileTypes: [],
      keyPolicyAreas: [],
      industries: [],
      committees: [],
      caucuses: [],
      agencies: [],
      city: "",
      education: "",
      includeFormer: false,
      // Birthdate
      birthYear: { id: "", name: "" },
      age: { id: "", name: "" },
      birthDateOperator: { id: "exact", name: "Born On" },
      // Special Interest Groups - NestedLookupTableItem but nullable
      sig: null,
      sigOperator: { id: "gte", name: "Greater Than" },
      sigRating: { id: "50", name: "50" },
    };
  }

  private static _getDefaultLookupTables(): LookupTables {
    return {
      subscriptionModules: [],
      editorialGroups: [],
      states: [],
      countries: [],
      primaryProfileTypes: [],
      secondaryProfileTypes: [],
      positionStatuses: [],
      genders: [],
      politicalParties: [],
      maritalStatuses: [],
      ethnicities: [],
      religions: [],
      industries: [],
      degrees: [],
      districts: [],

      // Flattened nested lookup tables
      profileTypesFlat: [],
      keyPolicyAreasFlat: [],
      industriesFlat: [],
      committeesFlat: [],
      caucusesFlat: [],
      agenciesFlat: [],
      specialInterestGroupsFlat: [],

      /** Generated & hardcoded fields **/

      // Birthdate
      birthYearOptions: [], // generated on init
      birthYearOperator: [
        { id: "exact", name: "Born On" },
        { id: "lt", name: "Born Before" },
        { id: "gte", name: "Born After" },
      ],
      ageOptions: [], // generated on init
      ageOperator: [
        { id: "exact", name: "Exact" },
        { id: "lt", name: "Younger Than" },
        { id: "gte", name: "Older Than" },
      ],

      // Special Interest Groups
      sigOperator: [
        { id: "lte", name: "Less Than" },
        { id: "gte", name: "Greater Than" },
      ],
      sigRatingOptions: [], // generated on init
    };
  }

  private static _getDefaultNestedLookupTables(): NestedLookupTables {
    return {
      profileTypes: [],
      keyPolicyAreas: [],
      industries: [],
      committees: [],
      caucuses: [],
      agencies: [],
      specialInterestGroups: [],
    };
  }

  //***** GETTERS *****//

  get getDefaultLookupTables(): LookupTables {
    return ApplicationModule._getDefaultLookupTables();
  }
  get getDefaultNestedLookupTables(): NestedLookupTables {
    return ApplicationModule._getDefaultNestedLookupTables();
  }

  get getBaseUrlPath(): string {
    return this.baseUrlPath;
  }

  get getBaseUrlOrigin(): string {
    return this.baseUrlOrigin;
  }

  get getPageName(): string {
    LogService.debug(`Getting ApplicationModule.getPageName: '${this.pageName || ""}'`);
    return this.pageName || "";
  }

  get getVignetteEmailAddress(): string {
    return this.vignetteEmailAddress;
  }

  get getVignetteEmailLink(): string {
    return "mailto:" + this.vignetteEmailAddress;
  }

  get isLoading(): boolean {
    return this.loading;
  }

  get isHomepageFeaturedProfilesLoading(): boolean {
    return this.homepageConfig.isLoading;
  }

  get isAutoCompleteSearchLoading(): boolean {
    return this.search.autocomplete.isLoading;
  }

  get isSearchLoading(): boolean {
    return this.search.isLoading;
  }

  get isNavDrawerExpanded(): boolean {
    return this.navDrawerExpanded;
  }

  get isAddedToBriefcaseById() {
    return (profileBriefcaseId: number): boolean => {
      return this.briefcase.itemIndex.get(profileBriefcaseId) != undefined;
    };
  }

  get isBriefcaseFolderExistsById() {
    return (folderId: number): boolean => {
      return this.briefcase.folders.get(folderId) != undefined;
    };
  }

  // Folder name should not be empty, and it should be unique.
  get isBriefcaseNewFolderNameUnique() {
    return (newFolderName: string): boolean => {
      return (
        !!newFolderName &&
        !!newFolderName.trim() &&
        this.briefcase.foldersFlat.filter(
          (folder) =>
            folder.name.localeCompare(newFolderName.trim(), undefined, {
              sensitivity: "base",
            }) === 0
        ).length === 0
      );
    };
  }

  get getRedirectPath(): string {
    return this.redirectPath;
  }

  get getHomepageConfigLastUpdated(): Date | null {
    return this.homepageConfig.lastUpdated;
  }

  get getHomepageFeaturedProfiles(): SearchItem[] {
    return this.homepageConfig.featuredProfiles;
  }

  get getHomepageFeaturedProfilesText(): string {
    return this.homepageConfig.featuredProfilesText;
  }

  get getHomepageRecentProfiles(): SearchItem[] {
    return this.homepageConfig.recentProfiles;
  }

  get getHomepageNoteworthyGroups(): NoteworthyGroup[] {
    return this.homepageConfig.noteworthyGroups;
  }

  get getPrimaryBanner(): Banner {
    return this.homepageConfig.primaryBanner;
  }

  get getSecondaryBanner(): Banner {
    return this.homepageConfig.secondaryBanner;
  }

  get getAutoCompleteSearchResults(): SearchItem[] {
    return this.search.autocomplete.results;
  }

  get getSearchCurrentQuery(): SearchQuery {
    return this.search.currentSearchQuery;
  }

  get getSearchLastQuery(): SearchQuery | null {
    return this.search.lastSearchQuery;
  }

  get getSearchLastQueryOrDefault(): SearchQuery {
    return this.search.lastSearchQuery || ApplicationModule._getDefaultSearchQuery();
  }

  get getSearchLastQueryView(): ProfileView {
    return this.search.lastSearchQuery?.view || ProfileView.DEFAULT;
  }

  get getSearchLastQueryPageNumber(): number {
    return this.search.lastSearchQueryPagination.page;
  }

  get getSearchLastQueryOrder(): SearchOrder {
    return this.search.lastSearchQueryPagination.order;
  }

  get getSearchLastQueryPageSize(): number {
    return this.search.lastSearchQueryPagination.pageSize;
  }

  get getSearchLastQueryPageCount(): number {
    return this.search.lastSearchQueryPagination.totalPages;
  }

  get getSearchLastQueryHitsCount(): number {
    return this.search.lastSearchQueryPagination.totalHits;
  }

  get getSearchCurrentPageItems(): SearchItem[] | null {
    return this.search.cachedResults[this.search.lastSearchQueryPagination.page];
  }

  get getSearchKeepCachedResults(): boolean {
    return this.search.keepCachedResults;
  }

  get isBriefcaseLoading(): boolean {
    return this.briefcase.isLoading;
  }

  get getBriefcaseCurrentPage(): number {
    return this.briefcase.currentPage;
  }

  get getBriefcaseLastUpdated(): Date | null {
    return this.briefcase.lastUpdated;
  }

  get getBriefcaseFolders(): BriefcaseFolder[] {
    // NOTE: this does not work reactively, because changed to Dictionary is not automatically picked up by VueX :(
    // return Array.from(this.briefcase.folders.values());
    // FIXME: if we can use `this.briefcase.folders` directly, we can delete `foldersFlat` and simplify
    // maybe try referencing variable and increment hack?
    return this.briefcase.foldersFlat;
  }

  get getBriefcaseFolderIdForId() {
    return (profileBriefcaseId: number): number => {
      const folders = this.briefcase.itemIndex.get(profileBriefcaseId) || [-1];
      return folders[0];
    };
  }

  get getBriefcaseFolderIdsForId() {
    return (profileBriefcaseId: number): number[] => {
      return this.briefcase.itemIndex.get(profileBriefcaseId) || [];
    };
  }

  get getBriefcaseCurrentFolder(): BriefcaseFolder {
    return this.briefcase.folders.get(this.briefcase.currentFolderId) as BriefcaseFolder;
  }

  get getBriefcaseCurrentFolderId(): number {
    return this.briefcase.currentFolderId;
  }

  get getBriefcaseCurrentDossierUrl(): string {
    return (this.briefcase.folders.get(this.briefcase.currentFolderId) as BriefcaseFolder).dossierUrl;
  }

  get getBriefcaseCurrentFolderName(): string {
    return (this.briefcase.folders.get(this.briefcase.currentFolderId) as BriefcaseFolder).name;
  }

  get getBriefcaseCurrentFolderItems(): BriefcaseItem[] {
    return this.briefcase.folders.has(this.briefcase.currentFolderId)
      ? // @ts-expect-error : compiler won't recognize we check if currentFolderId is valid above
        this.briefcase.folders.get(this.briefcase.currentFolderId).items
      : [];
  }

  get getBriefcaseCurrentFolderPageCount(): number {
    return Math.ceil(this.getBriefcaseCurrentFolderItemCount / this.briefcase.pageSize);
  }

  get getBriefcaseCurrentFolderItemCount(): number {
    return this.getBriefcaseCurrentFolderItems.length;
  }

  get getBriefcaseCurrentFolderCurrentPageItems(): BriefcaseItem[] {
    const start = (this.briefcase.currentPage - 1) * this.briefcase.pageSize;
    return this.getBriefcaseCurrentFolderItems.slice(start, start + this.briefcase.pageSize);
  }

  get getBriefcaseDefaultFolderId(): number {
    LogService.info(`ApplicationModule.getDefaultBriefcaseFolderId: getting default briefcase folder id.`);
    return this.briefcase.defaultFolderId;
  }

  get getBriefcaseItemBeingMoved(): SearchItem | null {
    return this.briefcase.itemBeingMoved;
  }

  get getLookupTable() {
    return (table: LookupTablesKeys): LookupTableItem[] => {
      return this.lookupTables[table];
    };
  }

  get getNestedLookupTable() {
    return (table: NestedLookupTablesKeys): NestedLookupTableItem[] => {
      return this.nestedLookupTables[table];
    };
  }

  get getLookupTableEntryByType() {
    return (table: LookupTablesKeys, searchType: LookupTableItemKeys, query: string): LookupTableItem => {
      return (
        this.lookupTables[table].find((item) => item[searchType] === query) || {
          id: "",
          name: "",
        }
      );
    };
  }

  get getNullableLookupTableEntryByType() {
    return (table: LookupTablesKeys, searchType: LookupTableItemKeys, query: string): LookupTableItem | null => {
      return this.lookupTables[table].find((item) => item[searchType] === query) || null;
    };
  }

  // TODO: not used, but this still might be needed if _FLAT lists are simplified
  get getNestedLookupTableEntryByType() {
    return (
      table: NestedLookupTablesKeys,
      searchType: NestedLookupTableItemKeys,
      query: string
    ): NestedLookupTableItem => {
      return (
        this.nestedLookupTables[table].find((item) => item[searchType] === query) || {
          id: "",
          name: "",
        }
      );
    };
  }

  get getSubscriptionModules(): SubscriptionModule[] {
    return this.lookupTables.subscriptionModules;
  }

  get getSubscriptionModuleName() {
    return (id: number): string | undefined => {
      if (this.lookupTables.subscriptionModules != null) {
        const module = this.lookupTables.subscriptionModules.find((module) => module.id === id);
        if (module != undefined) {
          return module.name;
        }
      }
    };
  }

  get getSubscriptionModuleAbbreviation() {
    return (id: number): string | undefined => {
      if (this.lookupTables.subscriptionModules != null) {
        const module = this.lookupTables.subscriptionModules.find((module) => module.id === id);
        if (module != undefined) {
          return module.abbreviation;
        }
      }
    };
  }

  get getAssignedUsersList(): string[] {
    return this.assignedUsersList;
  }

  get getSnackbarState(): boolean {
    return this.snackbarStatus.state;
  }

  get getSnackbarMessage(): string {
    return this.snackbarStatus.message;
  }

  get getSnackbarItemBeingMoved(): SearchItem | null {
    return this.snackbarStatus.itemBeingMoved;
  }

  get getSnackbarColor(): string {
    return this.snackbarStatus.color;
  }

  get getSnackbarTimeout(): number {
    return this.snackbarStatus.timeout;
  }

  get getUnlockedProfiles(): number[] {
    return this.unlockedProfiles;
  }

  get getTrialProfileLimit(): number {
    return this.trialProfileLimit;
  }

  get getSignUpFormStatusShouldKeep(): boolean {
    return this.signUpFormStatus.shouldKeep;
  }

  get getSignUpFormStatusEmail(): string {
    return this.signUpFormStatus.email;
  }

  get getSignUpFormStatusIsDisplayForm(): boolean {
    return this.signUpFormStatus.isDisplayForm;
  }

  //***** MUTATIONS - SYNCHRONOUS *****//

  // DEV REMINDERS:
  // Mutation functions MUST NOT be async functions.
  // Also do not define Mutation & Actions as arrow️ functions, since we need to rebind them at runtime.

  @Mutation
  setBaseUrlPath(newBaseUrl: string): void {
    LogService.debug(`Setting ApplicationModule.baseUrl to '${newBaseUrl}'`);
    this.baseUrlPath = newBaseUrl;
  }

  @Mutation
  setBaseUrlOrigin(newBaseOrigin: string): void {
    LogService.debug(`Setting ApplicationModule.baseOrigin to '${newBaseOrigin}'`);
    this.baseUrlOrigin = newBaseOrigin;
  }

  @Mutation
  setPageName(pageName: string): void {
    LogService.debug(`Setting ApplicationModule.setPageName to '${pageName}'`);
    this.pageName = pageName;
  }

  @Mutation
  setLoading(loading: boolean): void {
    LogService.debug(`Setting ApplicationModule.loading to '${loading}'`);
    this.loading = loading;
  }

  @Mutation
  setHomepageFeaturedProfilesLastUpdated(lastUpdated: Date): void {
    LogService.debug(`Setting ApplicationModule.setHomepageFeaturedProfilesLastUpdated to '${lastUpdated}'`);
    this.homepageConfig.lastUpdated = lastUpdated;
  }

  @Mutation
  setHomepageFeaturedProfilesLoading(loading: boolean): void {
    LogService.debug(`Setting ApplicationModule.setHomepageFeaturedProfilesLoading to '${loading}'`);
    this.homepageConfig.isLoading = loading;
  }

  @Mutation
  clearHomepageFeaturedProfiles(): void {
    LogService.debug(`Setting ApplicationModule.clearHomepageFeaturedProfiles`);
    this.homepageConfig.featuredProfiles = [];
    this.homepageConfig.recentProfiles = [];
    this.homepageConfig.noteworthyGroups = [];
  }

  @Mutation
  addToHomepageFeaturedProfiles(profile: SearchItem): void {
    LogService.debug(`Setting ApplicationModule.addToHomepageFeaturedProfiles for id: '${profile.id}'`);
    this.homepageConfig.featuredProfiles.push(profile);
  }

  @Mutation
  addToHomepageRecentProfiles(profile: SearchItem): void {
    LogService.debug(`Setting ApplicationModule.addToHomepageRecentProfiles for id: '${profile.id}'`);
    this.homepageConfig.recentProfiles.push(profile);
  }

  @Mutation
  addToHomepageNoteworthyGroups(group: NoteworthyGroup): void {
    LogService.debug(`Setting ApplicationModule.addToHomepageNoteworthyGroups for groupId: '${group.id}'`);
    this.homepageConfig.noteworthyGroups.push(group);
  }

  @Mutation
  addToHomepagePrimaryBanner(banner: Banner): void {
    LogService.debug(`Setting ApplicationModule.addToHomepagePrimaryBanner`);
    this.homepageConfig.primaryBanner = banner;
  }

  @Mutation
  addToHomepageSecondaryBanner(banner: Banner): void {
    LogService.debug(`Setting ApplicationModule.addToHomepageSecondaryBanner`);
    this.homepageConfig.secondaryBanner = banner;
  }

  @Mutation
  refreshHomepageFeaturedProfilesBriefcaseStatus(): void {
    this.homepageConfig.featuredProfiles.forEach((searchItem) => {
      searchItem.addedToBriefcase = this.briefcase.itemIndex.get(searchItem.briefcaseId) != undefined;
    });
    this.homepageConfig.recentProfiles.forEach((searchItem) => {
      searchItem.addedToBriefcase = this.briefcase.itemIndex.get(searchItem.briefcaseId) != undefined;
    });
  }

  @Mutation
  refreshSearchResultProfilesBriefcaseStatus(): void {
    this.search.cachedResults.forEach((itemList) =>
      itemList.forEach((searchItem) => {
        searchItem.addedToBriefcase = this.briefcase.itemIndex.get(searchItem.briefcaseId) != undefined;
      })
    );
  }

  @Mutation
  setFeaturedProfilesText(text: string): void {
    this.homepageConfig.featuredProfilesText = text;
  }

  @Mutation
  setAutoCompleteSearchLoading(loading: boolean): void {
    LogService.debug(`Setting ApplicationModule.setAutoCompleteSearchLoading to '${loading}'`);
    this.search.autocomplete.isLoading = loading;
  }

  @Mutation
  setAutoCompleteSearchResults(results: SearchItem[]): void {
    LogService.debug(`Setting ApplicationModule.setAutoCompleteSearchResults for results.length: '${results.length}'`);
    this.search.autocomplete.results = results;
  }

  @Mutation
  setSearchLastQuery(previousQuery: SearchQuery): void {
    LogService.debug(`Setting ApplicationModule.setLastSearchQuery to '${JSON.stringify(previousQuery)}'`);
    this.search.lastSearchQuery = previousQuery;
  }

  @Mutation
  setSearchLastQueryField(payload: SearchQueryUpdatePayload): void {
    LogService.debug(`Setting ApplicationModule.setSearchLastQueryField to '${JSON.stringify(payload)}'`);
    if (this.search.lastSearchQuery != null) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.search.lastSearchQuery[payload.field] = payload.value;
    }
  }

  @Mutation
  setSearchCurrentQuery(currentQuery: SearchQuery): void {
    LogService.debug(`Setting ApplicationModule.setSearchCurrentQuery to '${JSON.stringify(currentQuery)}'`);
    this.search.currentSearchQuery = currentQuery;
  }

  @Mutation
  setSearchCurrentQueryField(payload: SearchQueryUpdatePayload): void {
    LogService.debug(`Setting ApplicationModule.setSearchCurrentQueryField to '${JSON.stringify(payload)}'`);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.search.currentSearchQuery[payload.field] = payload.value;
  }

  @Mutation
  setSearchLoading(loading: boolean): void {
    LogService.debug(`Setting ApplicationModule.searchLoading to '${loading}'`);
    this.search.isLoading = loading;
  }

  @Mutation
  setSearchPageNumber(pageNumber: number): void {
    LogService.debug(`Setting ApplicationModule.setSearchPageNumber to '${pageNumber}'`);
    this.search.lastSearchQueryPagination.page = pageNumber;
  }

  @Mutation
  setSearchPageSize(pageSize: number): void {
    LogService.debug(`Setting ApplicationModule.setSearchPageSize to '${pageSize}'`);
    // Cap this at 100 profiles. Prevents trying to load too many profiles at once.
    this.search.lastSearchQueryPagination.pageSize = Math.min(pageSize, 100);
  }

  @Mutation
  setSearchOrder(order: SearchOrder): void {
    LogService.debug(`Setting ApplicationModule.setSearchOrder to '${order}'`);
    this.search.lastSearchQueryPagination.order = order;
  }

  @Mutation
  setSearchFlipOrder(): void {
    const from: SearchOrder = this.search.lastSearchQueryPagination.order;
    const to: SearchOrder = from === SearchOrder.ALPHABETICAL ? SearchOrder.RELEVANCE : SearchOrder.ALPHABETICAL;
    LogService.debug(`Setting ApplicationModule.setSearchFlipOrder from '${from}' to '${to}'`);
    this.search.lastSearchQueryPagination.order = to;
  }

  @Mutation
  setSearchKeepCachedResults(keepCachedResults: boolean): void {
    LogService.debug(`Setting ApplicationModule.setSearchKeepCachedResults to '${keepCachedResults}'`);
    this.search.keepCachedResults = keepCachedResults;
  }

  @Mutation
  setNavDrawerExpanded(navDrawerExpanded: boolean): void {
    LogService.debug(`Setting ApplicationModule.navDrawerExpanded to '${navDrawerExpanded}'`);
    this.navDrawerExpanded = navDrawerExpanded;
  }

  @Mutation
  setRedirectPath(redirectPath: string): void {
    LogService.debug(`Setting ApplicationModule.redirectPath to '${redirectPath}'`);
    this.redirectPath = redirectPath;
  }

  @Mutation
  setSearchResultsPagination(pagination: SearchResultsPagination): void {
    // Make sure both setSearchResultsPagination and setSearchResults are called at the same time, and are in sync
    LogService.debug(`Setting ApplicationModule.setSearchResultsPagination: '${JSON.stringify(pagination)}'`);
    this.search.lastSearchQueryPagination = pagination;
  }

  @Mutation
  setSearchResults(results: SearchItem[]): void {
    // Make sure both setSearchResultsPagination and setSearchResults are called at the same time, and are in sync
    LogService.debug(`Setting ApplicationModule.setSearchResults for results.length: '${results.length}'`);
    this.search.cachedResults[this.search.lastSearchQueryPagination.page] = results;
  }

  @Mutation
  resetSearch(): void {
    LogService.debug(`Setting ApplicationModule.resetSearch to '(empty|null)'`);
    this.search = ApplicationModule._getDefaultSearch();
  }

  @Mutation
  setBriefcaseLoading(loading: boolean): void {
    LogService.debug(`Setting ApplicationModule.setBriefcaseLoading to '${loading}'`);
    this.briefcase.isLoading = loading;
  }

  @Mutation
  setBriefcaseFolders(briefcaseFolders: BriefcaseFolder[]): void {
    LogService.debug(`Setting ApplicationModule.setBriefcaseFolders.'`);

    if (briefcaseFolders == null || briefcaseFolders.length == 0) {
      LogService.warn(`ApplicationModule.setBriefcaseFolders: briefcase folder list is empty.`);
      return;
    }

    // Reset currentFolderId if current selected folder does not exist in the new list
    if (
      this.briefcase.currentFolderId !== -1 &&
      briefcaseFolders.filter((folder) => folder.id === this.briefcase.currentFolderId).length === 0
    ) {
      this.briefcase.currentFolderId = -1;
      this.briefcase.currentPage = 1;
    }

    for (const briefcaseFolder of briefcaseFolders) {
      this.briefcase.folders.set(briefcaseFolder.id, briefcaseFolder);
    }

    this.briefcase.foldersFlat = briefcaseFolders.sort(BriefcaseComparator);

    // It should be reset already, but just to be safe.
    this.briefcase.itemIndex = new Map<number, number[]>();

    // Set default folder to current folder, and populate itemIndex for quick lookup.
    for (const briefcaseFolder of this.briefcase.folders.values()) {
      // The itemIndex controls where "unstaring" removes a profile from.
      // We want to prioritise this to set the default folder.

      for (const briefcaseItem of briefcaseFolder.items) {
        // Adding briefcase item to itemIndex.
        let folderIds: number[] | undefined = this.briefcase.itemIndex.get(briefcaseItem.briefcaseId);
        if (folderIds === undefined) {
          folderIds = [briefcaseFolder.id];
        } else if (folderIds.indexOf(briefcaseFolder.id) < 0) {
          folderIds.push(briefcaseFolder.id);
        }
        this.briefcase.itemIndex.set(briefcaseItem.briefcaseId, folderIds);
      }

      if (briefcaseFolder.isDefault) {
        this.briefcase.defaultFolderId = briefcaseFolder.id;

        // Go to default folder if current folder is not yet initialized (on load)
        if (this.briefcase.currentFolderId === -1) {
          this.briefcase.currentFolderId = briefcaseFolder.id;
        }
      }
    }

    // Just in case - make sure a folder is selected after updating the list
    if (this.briefcase.currentFolderId === -1) {
      LogService.warn(`ApplicationModule.setBriefcaseFolders: briefcase folders has no default.`);
      this.briefcase.currentFolderId = this.briefcase.defaultFolderId = briefcaseFolders[0].id;
    }
    // In case current page is no longer available (after refresh), go to previous available page:
    // TODO: implement this
  }

  @Mutation
  setBriefcaseFolder(folderId: number): void {
    LogService.info(`ApplicationModule.setBriefcaseFolder: setting briefcase folder id to '${folderId}'`);
    this.briefcase.currentFolderId = folderId;
  }

  @Mutation
  addBriefcaseFolder(folder: BriefcaseFolder): void {
    LogService.info(`ApplicationModule.addBriefcaseFolder: adding new briefcase folder with id: '${folder.id}'`);
    this.briefcase.folders.set(folder.id, folder);
    this.briefcase.foldersFlat.push(folder);
    this.briefcase.foldersFlat.sort(BriefcaseComparator);
  }

  @Mutation
  removeBriefcaseFolder(folderId: number): void {
    LogService.info(`ApplicationModule.removeBriefcaseFolder: removing briefcase folder with id: '${folderId}'`);
    this.briefcase.folders.delete(folderId);
    this.briefcase.foldersFlat = this.briefcase.foldersFlat.filter((folder) => folder.id !== folderId);
  }

  @Mutation
  addToBriefcaseItemIndex(payload: { itemId: number; newFolderId: number }): void {
    const itemId = payload.itemId;
    const newFolderId = payload.itemId;
    let folderIds: number[] | undefined = this.briefcase.itemIndex.get(itemId);
    if (folderIds === undefined) {
      folderIds = [newFolderId];
    } else if (folderIds.indexOf(newFolderId) < 0) {
      folderIds.push(newFolderId);
    }
    this.briefcase.itemIndex.set(itemId, folderIds);
  }

  @Mutation
  removeFromBriefcaseItemIndex(payload: { itemId: number; newFolderId: number }): void {
    // Determine what the list of folder Ids should be.
    const itemId = payload.itemId;
    const newFolderId = payload.itemId;
    let folderIds: number[] | undefined = this.briefcase.itemIndex.get(itemId);
    if (folderIds) {
      folderIds = folderIds.filter((folder: number) => folder === newFolderId);
    } else {
      folderIds = [];
    }

    // Delete if folder Ids are length zero otherwise update the value.
    if (folderIds.length === 0) {
      this.briefcase.itemIndex.delete(itemId);
    } else {
      this.briefcase.itemIndex.set(itemId, folderIds);
    }
  }

  @Mutation
  addToBriefcase(payload: BriefcasePayload): void {
    if (payload.folderId == undefined) {
      LogService.debug(`ApplicationModule.addToBriefcase: setting folderId to default folder.`);
      payload.folderId = this.briefcase.defaultFolderId;
    }
    LogService.debug(
      `ApplicationModule.addToBriefcase for profileIds: '${JSON.stringify(
        payload.items.map((item: BriefcaseItem) => item.briefcaseId)
      )}' into folder: '${payload.folderId}'`
    );

    const briefcaseFolder: BriefcaseFolder | undefined = this.briefcase.folders.get(payload.folderId);

    if (briefcaseFolder) {
      const profileIds = briefcaseFolder.items.map((item) => item.briefcaseId);

      for (const item of payload.items) {
        // Add briefcaseItem to beginning of array to match backend behavior.
        if (profileIds.indexOf(item.briefcaseId) < 0) {
          briefcaseFolder.items.unshift(item);
        }

        // Adding items to itemIndex.
        let folderIds: number[] | undefined = this.briefcase.itemIndex.get(item.briefcaseId);
        if (folderIds === undefined) {
          folderIds = [briefcaseFolder.id];
        } else if (folderIds.indexOf(briefcaseFolder.id) < 0) {
          folderIds.push(briefcaseFolder.id);
        }
        this.briefcase.itemIndex.set(item.briefcaseId, folderIds);
      }

      // TODO: deduplicate this function
      this.briefcase.foldersFlat = Array.from(this.briefcase.folders.values()).sort(BriefcaseComparator);
    } else {
      LogService.warn(`ApplicationModule.addToBriefcase: cannot find folder: '${payload.folderId}'`);
    }
  }

  @Mutation
  removeFromBriefcase(payload: BriefcasePayload): void {
    if (payload.folderId == undefined) {
      LogService.debug(`ApplicationModule.removeFromBriefcase: setting folderId to default folder.`);
      payload.folderId = this.briefcase.defaultFolderId;
    }
    LogService.debug(
      `ApplicationModule.removeFromBriefcase for profileId: ${JSON.stringify(
        payload.items.map((item: BriefcaseItem) => item.briefcaseId)
      )} from folder: '${payload.folderId}'`
    );

    const briefcaseFolder: BriefcaseFolder | undefined = this.briefcase.folders.get(payload.folderId);
    if (briefcaseFolder) {
      for (const item of payload.items) {
        briefcaseFolder.items = briefcaseFolder.items.filter(
          (briefcaseItem) => briefcaseItem.briefcaseId !== item.briefcaseId
        );

        // Removing just that folder from itemIndex. If it was the last briefcase,
        // remove it from the itemIndex all together.
        let folderIds: number[] | undefined = this.briefcase.itemIndex.get(item.briefcaseId);
        if (folderIds) {
          folderIds = folderIds.filter((folder: number) => folder !== payload.folderId);
        } else {
          folderIds = [];
        }

        // Delete if folder Ids are length zero otherwise update the value.
        if (folderIds.length === 0) {
          this.briefcase.itemIndex.delete(item.briefcaseId);
        } else {
          this.briefcase.itemIndex.set(item.briefcaseId, folderIds);
        }
      }

      // TODO: deduplicate this function
      this.briefcase.foldersFlat = Array.from(this.briefcase.folders.values()).sort(BriefcaseComparator);
    } else {
      LogService.warn(`ApplicationModule.removeFromBriefcase: cannot find folder: '${payload.folderId}'`);
    }
  }

  @Mutation
  setBriefcaseItemBeingMoved(item: SearchItem | null): void {
    // itemBeingMoved is the search item that the BriefcaseFolderMoveDialog
    // uses to determine the profile in question. As well as containing the
    // information needed for the display and setting ids around, this is also
    // the trigger to determine whether to show the BriefcaseFolderMoveDialog.
    // By setting it to a searchItem, the dialog will appear. When you set this
    // to null, then the dialog will be closed.
    this.briefcase.itemBeingMoved = item;
    LogService.debug(`Setting ApplicationModule.setBriefcaseItemBeingMoved to '${item?.id || null}'`);
  }

  @Mutation
  setSnackbarItemBeingMoved(item: SearchItem | null): void {
    // The snackbarSearchItem is used to help to prime the itemBeingMoved.
    // We need to store it separately. After a profile is starred and the profile
    // gets put into their general briefcase, this function gets called to
    // "remember" what was just selected. Then, when the user clicks "select folders"
    // this value gets copied into itemBeingMoved.
    this.snackbarStatus.itemBeingMoved = item;
    LogService.debug(`Setting ApplicationModule.setSnackbarItemBeingMoved to '${item?.id || null}'`);
  }

  @Mutation
  setBriefcaseCurrentPage(pageNumber: number): void {
    LogService.debug(`Setting ApplicationModule.setBriefcaseCurrentPage to '${pageNumber}'`);
    this.briefcase.currentPage = pageNumber;
  }

  @Mutation
  setBriefcaseLastUpdated(lastUpdated: Date): void {
    LogService.debug(`Setting ApplicationModule.setBriefcaseLastUpdated to '${lastUpdated}'`);
    this.briefcase.lastUpdated = lastUpdated;
  }

  @Mutation
  setUnlockedProfiles(unlockedProfiles: number[]): void {
    LogService.debug(`Setting ApplicationModule.setUnlockedProfiles.`);
    this.unlockedProfiles = unlockedProfiles;
  }

  @Mutation
  setLookupTables(lookupTables: LookupTables): void {
    LogService.debug(`Setting ApplicationModule.setLookupTables.`);

    this.lookupTables.subscriptionModules = lookupTables.subscriptionModules;
    this.lookupTables.editorialGroups = lookupTables.editorialGroups;
    this.lookupTables.states = lookupTables.states;
    this.lookupTables.countries = lookupTables.countries;
    this.lookupTables.primaryProfileTypes = lookupTables.primaryProfileTypes;
    this.lookupTables.secondaryProfileTypes = lookupTables.secondaryProfileTypes;
    this.lookupTables.positionStatuses = lookupTables.positionStatuses;
    this.lookupTables.genders = lookupTables.genders;
    this.lookupTables.politicalParties = lookupTables.politicalParties;
    this.lookupTables.maritalStatuses = lookupTables.maritalStatuses;
    this.lookupTables.ethnicities = lookupTables.ethnicities;
    this.lookupTables.religions = lookupTables.religions;
    this.lookupTables.industries = lookupTables.industries;
    this.lookupTables.degrees = lookupTables.degrees;
    this.lookupTables.districts = lookupTables.districts;
  }

  @Mutation
  setNestedLookupTables(nestedLookupTables: NestedLookupTables): void {
    LogService.debug(`Setting ApplicationModule.setNestedLookupTables.`);

    Object.values(NestedLookupTablesKeys).forEach((value) => {
      const flattened: LookupTableItem[] = [];

      // Assumed the list is always up to 2 levels deep
      for (const nestedItem of nestedLookupTables[value]) {
        flattened.push(nestedItem);

        if (nestedItem.children != null && nestedItem.children.length > 0) {
          for (const nestedItemChild of nestedItem.children) {
            flattened.push(nestedItemChild);
          }
        }
      }

      // Value is something like "keyPolicyAreas", in lookupTable it matches "keyPolicyAreasFlat"
      this.nestedLookupTables[value] = nestedLookupTables[value];
      this.lookupTables[(value + "Flat") as LookupTablesKeys] = flattened;
    });
  }

  @Mutation
  setLookupTablesGeneratedFields(): void {
    LogService.debug(`Setting ApplicationModule.setLookupTablesGeneratedFields.`);
    for (let year = 2020; year >= 1920; year--) {
      this.lookupTables.birthYearOptions.push({
        id: `${year}`,
        name: `${year}`,
      });
    }
    for (let age = 18; age <= 100; age++) {
      this.lookupTables.ageOptions.push({
        id: `${age}`,
        name: `${age}`,
      });
    }

    for (let score = 0; score <= 100; score += 10) {
      this.lookupTables.sigRatingOptions.push({
        id: `${score}`,
        name: `${score}%`,
      });
    }
  }

  @Mutation
  setAssignedUsersList(assignedUsers: string[]): void {
    LogService.debug(`Setting ApplicationModule.setAssignedUsersList to count: ${assignedUsers.length}`);
    this.assignedUsersList = assignedUsers;
  }

  @Mutation
  setSnackbarState(state: boolean): void {
    LogService.debug(`Setting ApplicationModule.setSnackbarState to '${state}'`);
    this.snackbarStatus.state = state;
  }

  @Mutation
  setSnackbarMessage(message: string): void {
    LogService.debug(`Setting ApplicationModule.setSnackbarMessage to '${message}'`);
    this.snackbarStatus.message = message;
  }

  @Mutation
  setSnackbarColor(color: string): void {
    LogService.debug(`Setting ApplicationModule.setSnackbarColor to '${color}'`);
    this.snackbarStatus.color = color;
  }

  @Mutation
  setSnackbarTimeout(timeout: number): void {
    LogService.debug(`Setting ApplicationModule.setSnackbarTimeout to '${timeout}'`);
    this.snackbarStatus.timeout = timeout;
  }

  @Mutation
  setSignUpFormStatusShouldKeep(shouldKeep: boolean): void {
    this.signUpFormStatus.shouldKeep = shouldKeep;
  }

  @Mutation
  setSignUpFormStatusEmail(email: string): void {
    this.signUpFormStatus.email = email;
  }

  @Mutation
  setSignUpFormStatusIsDisplayForm(isDisplayForm: boolean): void {
    this.signUpFormStatus.isDisplayForm = isDisplayForm;
  }

  //***** ACTIONS - ASYNCHRONOUS *****//

  // DEV REMINDERS:
  // Mutation functions MUST NOT be async functions.
  // Also do not define Mutation & Actions as arrow️ functions, since we need to rebind them at runtime.

  // FIXME: not used
  // @Action
  // public async appLoading(): Promise<void> {
  //   LogService.debug(`ApplicationModule.appLoading is called.`);
  //   await new Promise((resolve) => setTimeout(() => resolve(), 1000));
  //   this.setLoading(true);
  // }

  // FIXME: not used
  // @Action
  // public async doneLoading(): Promise<void> {
  //   LogService.debug(`ApplicationModule.doneLoading is called.`);
  //   await new Promise((resolve) => setTimeout(() => resolve(), 1000));
  //   this.setLoading(false);
  // }
}
